import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { CheckCircle2, ArrowUpRight, Gift, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShopSuccessPage() {
  const [params] = useSearchParams();
  const { lang } = useLang();
  const [status, setStatus] = useState(null);
  const sessionId = params.get("session_id");

  // Referral state
  const [email, setEmail] = useState("");
  const [referral, setReferral] = useState(null); // { code, share_url, ... }
  const [refBusy, setRefBusy] = useState(false);
  const autoMintStarted = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    let cancel = false;
    let n = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/shop/checkout/${sessionId}`);
        if (cancel) return;
        setStatus(data);
        if (data.customer_email && !email) setEmail(data.customer_email);
        if (data.payment_status !== "paid" && n < 10) {
          n += 1;
          setTimeout(poll, 1500);
        }
      } catch {
        /* ignore */
      }
    };
    poll();
    return () => { cancel = true; };
  }, [sessionId]);

  const paid = status?.payment_status === "paid";

  // Auto-mint referral once the payment is paid AND we know the buyer's email.
  useEffect(() => {
    if (!paid || !email || referral || autoMintStarted.current) return;
    autoMintStarted.current = true;
    let cancel = false;
    (async () => {
      setRefBusy(true);
      try {
        const { data } = await axios.post(`${BACKEND_URL}/api/referrals`, { email });
        if (!cancel) setReferral(data);
      } catch {
        /* silently ignore — the manual "Get my link" button still works */
        autoMintStarted.current = false; // allow retry via the button
      } finally {
        if (!cancel) setRefBusy(false);
      }
    })();
    return () => { cancel = true; };
  }, [paid, email, referral]);

  const mintManually = async () => {
    if (!email.trim() || refBusy) return;
    setRefBusy(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/referrals`, { email: email.trim() });
      setReferral(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create link");
    } finally {
      setRefBusy(false);
    }
  };

  const copyLink = async () => {
    if (!referral?.share_url) return;
    try {
      await navigator.clipboard.writeText(referral.share_url);
      toast.success(t(lang, "referral_prompt_copied"));
    } catch {
      /* clipboard blocked → fall back to prompt */
      window.prompt("Copy your link:", referral.share_url);
    }
  };

  const whatsappShareUrl = referral?.share_url
    ? `https://wa.me/?text=${encodeURIComponent(`${t(lang, "referral_prompt_title")} — ${referral.share_url}`)}`
    : "#";

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6" data-testid="shop-success-page">
      <div className="max-w-md w-full text-center">
        <div className={`w-16 h-16 mx-auto rounded-full border flex items-center justify-center mb-6 ${
          paid ? "border-[color:var(--hq-success)] text-[color:var(--hq-success)]" : "border-white/20 text-white/40"
        }`}>
          <CheckCircle2 size={32} />
        </div>
        <h1 className="font-display font-light text-3xl tracking-tight">
          {paid ? t(lang, "shop_success_title") : t(lang, "shop_success_pending")}
        </h1>
        <p className="mt-3 text-white/60 text-sm">
          {paid ? t(lang, "shop_success_body") : t(lang, "shop_success_pending_body")}
        </p>
        {status && (
          <div className="mt-6 font-mono text-[10px] uppercase tracking-widest text-white/40">
            <span className="text-white/30 mr-1">order ref</span>
            {status.session_id?.slice(-14)}
          </div>
        )}

        {/* Post-purchase referral prompt */}
        {paid && (
          <div
            data-testid="post-purchase-referral-prompt"
            className="mt-8 text-left rounded-lg border border-[color:var(--hq-cyan)]/30 bg-[color:var(--hq-cyan)]/[0.06] p-5"
          >
            <div className="flex items-center gap-2 mb-2">
              <Gift size={16} className="text-[color:var(--hq-cyan)]" />
              <div className="font-display text-base">{t(lang, "referral_prompt_title")}</div>
            </div>
            <p className="text-white/70 text-xs leading-relaxed">
              {t(lang, "referral_prompt_body")}
            </p>

            {!referral ? (
              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <input
                  data-testid="referral-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t(lang, "referral_prompt_email_placeholder")}
                  className="hq-input flex-1 text-sm"
                />
                <button
                  data-testid="referral-generate-btn"
                  onClick={mintManually}
                  disabled={refBusy || !email.trim()}
                  className="hq-btn hq-btn-primary disabled:opacity-50"
                >
                  {t(lang, "referral_prompt_generate")}
                </button>
              </div>
            ) : (
              <div className="mt-4 space-y-2">
                <div
                  data-testid="referral-share-url"
                  className="rounded-md border border-white/10 bg-black/40 px-3 py-2 font-mono text-[11px] break-all"
                >
                  {referral.share_url}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid="referral-copy-btn"
                    onClick={copyLink}
                    className="hq-btn hq-btn-ghost text-xs"
                  >
                    <Copy size={12} /> {t(lang, "referral_prompt_copy")}
                  </button>
                  <a
                    data-testid="referral-share-whatsapp"
                    href={whatsappShareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hq-btn hq-btn-ghost text-xs"
                  >
                    <Share2 size={12} /> {t(lang, "referral_prompt_share")}
                  </a>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/shop" data-testid="shop-back-to-catalog" className="hq-btn hq-btn-primary">
            {t(lang, "shop_back_to_catalog")} <ArrowUpRight size={14} />
          </Link>
          {status?.session_id && (
            <Link
              to={`/shop/withdrawal?order=${encodeURIComponent(status.session_id)}`}
              data-testid="shop-success-withdrawal-link"
              className="hq-btn hq-btn-ghost"
            >
              {t(lang, "wd_button_from_order")}
            </Link>
          )}
        </div>
        <p className="mt-4 font-mono text-[10px] uppercase text-white/40">
          {t(lang, "wd_14day_reminder")}
        </p>
      </div>
    </div>
  );
}
